/**
 * Correction À LA MAIN d'une quête partagée : ce qu'on peut réécrire, et où.
 *
 * Miroir de tc_learn `engine/body_correction.py` — c'est le serveur qui fait
 * foi (il refuse en 422 toute correction qui touche la structure) ; ici on ne
 * propose que ce qu'il acceptera :
 *
 * - les chaînes des champs de TEXTE du contrat (`TEXT_FIELDS`) : consigne,
 *   options, justifications, explication, débriefs, accroche, libellés,
 *   réponses écrites d'un tableau… ;
 * - la CLÉ d'un choix (`is_correct`) : quelle option est la bonne.
 *
 * Tout le reste (identifiants, forme, ordre attendu, nombre d'options) est
 * figé : une option en moins ou un identifiant renommé casserait l'écran de
 * chaque élève qui recevra le clone.
 */

/** Même liste que `TEXT_FIELDS` de tc_learn `engine/body_correction.py`. */
export const TEXT_FIELDS: ReadonlySet<string> = new Set([
  "narrative_hook",
  "narrative_debrief",
  "narrative_debrief_success",
  "narrative_debrief_failure",
  "pass_criteria",
  "prompt",
  "payload",
  "rationale",
  "explanation",
  "label",
  "name",
  "date",
  "left",
  "right",
  "relation",
  "value",
  "term_a",
  "term_b",
  "term_c",
  "term_d",
  "headers",
  "choices",
  "answers",
  "accepted_answers",
]);

/** La clé d'un choix (QCM, carte de concepts). */
export const KEY_FIELDS: ReadonlySet<string> = new Set(["is_correct"]);

export type Path = Array<string | number>;

export interface EditableField {
  path: Path;
  kind: "text" | "key";
  /** Libellé lisible du chemin (« Étape 1 · option B · justification »). */
  label: string;
  value: string | boolean;
  /** Texte long : zone multiligne. */
  multiline: boolean;
}

const FIELD_LABELS: Record<string, string> = {
  narrative_hook: "accroche",
  narrative_debrief: "débrief",
  narrative_debrief_success: "débrief (réussite)",
  narrative_debrief_failure: "débrief (échec)",
  pass_criteria: "critère de réussite",
  prompt: "consigne",
  payload: "texte",
  rationale: "justification",
  explanation: "explication",
  label: "libellé",
  name: "nom",
  date: "date",
  left: "gauche",
  right: "droite",
  relation: "relation",
  value: "case",
  term_a: "terme A",
  term_b: "terme B",
  term_c: "terme C",
  term_d: "terme D",
  headers: "en-tête",
  choices: "choix",
  answers: "réponse",
  accepted_answers: "réponse acceptée",
  is_correct: "bonne réponse",
};

/** Un segment de chemin, en français, pour l'humain qui corrige. */
const segmentLabel = (parent: string | undefined, key: string | number, node: unknown): string | null => {
  if (typeof key === "number") {
    if (parent === "question_chain") return `Étape ${key + 1}`;
    const ident =
      node && typeof node === "object"
        ? ((node as Record<string, unknown>).option_id ?? (node as Record<string, unknown>).id)
        : undefined;
    if (parent === "options") return `option ${ident ?? key + 1}`;
    if (parent === "rows") return `ligne ${key + 1}`;
    if (parent === "cells") return `colonne ${key + 1}`;
    return ident ? String(ident) : `n° ${key + 1}`;
  }
  if (key === "expected_answer_spec" || key === "evaluation_grid") return null;
  if (parent === "answers" || parent === "accepted_answers") return `case ${key}`;
  return FIELD_LABELS[key] ?? null;
};

/**
 * Les champs réécrivables du corps, dans l'ordre de lecture. Pure.
 *
 * Une chaîne n'est proposée que sous un champ de texte (directement, dans une
 * liste ou dans un dictionnaire) ; un booléen seulement sous `is_correct`.
 */
export const editableFields = (body: unknown): EditableField[] => {
  const out: EditableField[] = [];
  const walk = (node: unknown, path: Path, labels: string[], text: boolean, field: string | undefined): void => {
    if (Array.isArray(node)) {
      node.forEach((child, i) => {
        const seg = segmentLabel(field, i, child);
        walk(child, [...path, i], seg ? [...labels, seg] : labels, text, field);
      });
      return;
    }
    if (node && typeof node === "object") {
      Object.entries(node as Record<string, unknown>).forEach(([key, child]) => {
        const seg = segmentLabel(field, key, child);
        walk(child, [...path, key], seg ? [...labels, seg] : labels, text || TEXT_FIELDS.has(key), key);
      });
      return;
    }
    if (text && typeof node === "string") {
      out.push({ path, kind: "text", label: labels.join(" · "), value: node, multiline: node.length > 60 });
    } else if (field !== undefined && KEY_FIELDS.has(field) && typeof node === "boolean") {
      out.push({ path, kind: "key", label: labels.join(" · "), value: node, multiline: false });
    }
  };
  walk(body, [], [], false, undefined);
  return out;
};

/** Copie du corps avec `value` posée au chemin donné. Pure. */
export const setAt = <T,>(body: T, path: Path, value: unknown): T => {
  if (path.length === 0) return value as T;
  const [head, ...rest] = path;
  if (Array.isArray(body)) {
    const copy = [...body];
    copy[head as number] = setAt(copy[head as number], rest, value);
    return copy as unknown as T;
  }
  const obj = { ...(body as Record<string, unknown>) };
  obj[head as string] = setAt(obj[head as string], rest, value);
  return obj as T;
};

/** Clé de React (et de formulaire) d'un chemin. */
export const pathKey = (path: Path): string => path.join("/");
